import { formatPublicSourceLabel } from "@/features/machines/addressing";
import type { DashboardWidget, ResolvedWidgetValue } from "../../types";
import { formatWidgetNumber, formatWidgetRelative, getClampedPercent } from "./format";
import { HeroCard, HeroMeaning } from "./HeroCard";
import { effectiveQuality } from "./quality";
import { describeReadingState, describeThreshold } from "./readingMeaning";

interface BarWidgetProps {
  widget: DashboardWidget;
  value: ResolvedWidgetValue;
  editable?: boolean;
  onConfigure?: () => void;
  onGenerateChart?: () => void;
}

/**
 * Progresso no cartão herói: o valor é o herói; a barra (posição na escala
 * min/max) é a FACE. Silêncio quando OK; só fala num limite.
 */
export function BarWidget({
  widget,
  value,
  editable,
  onConfigure,
  onGenerateChart,
}: BarWidgetProps) {
  const min = widget.settings.min ?? 0;
  const max = widget.settings.max ?? 100;
  const decimals = widget.settings.decimals ?? 1;
  const unit = widget.settings.unit?.trim() || null;
  const available = value.isAvailable && effectiveQuality(value) === "live";
  const rangeBroken =
    !Number.isFinite(min) || !Number.isFinite(max) || max <= min;
  const fillClass = value.tone === "warn" ? "warn" : value.tone === "danger" ? "danger" : "ok";
  const percent = available && !rangeBroken ? getClampedPercent(value.numericValue, min, max) : 0;
  const meaning = describeReadingState(value) ?? describeThreshold(widget, value);
  const cardTone: "ok" | "warn" | "danger" =
    meaning?.tone === "danger"
      ? "danger"
      : meaning?.tone === "warn" || rangeBroken
        ? "warn"
        : "ok";
  const caption = formatPublicSourceLabel(widget.source, widget.settings.signalKind);

  return (
    <HeroCard
      title={widget.title}
      caption={caption !== widget.title ? caption : null}
      tone={cardTone}
      alarm={widget.settings.alarmEnabled}
      editable={editable}
      onConfigure={onConfigure}
      onGenerateChart={onGenerateChart}
      footnote={available ? `Atualizado ${formatWidgetRelative(value.measuredAt) ?? "agora"}` : null}
    >
      <div className="w-hero__value">
        {available ? formatWidgetNumber(value.numericValue, decimals) : "--"}
        {unit ? <span className="w-hero__unit">{unit}</span> : null}
      </div>

      {meaning ? <HeroMeaning tone={meaning.tone}>{meaning.text}</HeroMeaning> : null}

      {rangeBroken ? (
        <div className="w-hero__bar w-hero__scale-error" role="status">
          SEM ESCALA
        </div>
      ) : (
        <div className="w-hero__bar">
          <div className="bar-track">
            <div className={`bar-fill ${fillClass}`} style={{ width: `${percent}%` }} />
          </div>
          <div className="bar-scale">
            <span>
              {min}
              {unit ?? ""}
            </span>
            <span>
              {max}
              {unit ?? ""}
            </span>
          </div>
        </div>
      )}
    </HeroCard>
  );
}
